import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Plus,
  Minus,
  Trash,
  Receipt,
  Download,
  Tag,
} from '@phosphor-icons/react';
import api from '../../utils/api';
import { useBusiness } from '../../context/BusinessContext';
import { useBusinessSubscription } from '../../hooks/useBusinessSubscription';
import { formatGBP, formatGBPFromPounds } from '../../utils/currency';
import { getPriceSuffix } from '../../utils/priceDisplay';
import useThemeStyles from '../../theme/useThemeStyles';
import BusinessSubscriptions from './BusinessSubscriptions';
import { useAuth } from '../../context/AuthContext';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CheckIcon from '@mui/icons-material/CheckCircle';
import Stack from '@mui/material/Stack';
import LegalDocumentModal from '../Legal/LegalDocumentModal';

function BusinessBilling() {
  const styles = useThemeStyles();
  const { selectedBusiness, hasPermission } = useBusiness();
  const { _isTrial, trialEndsAt: _trialEndsAt } = useBusinessSubscription();
  const businessId = selectedBusiness?.business?._id;
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [addons, setAddons] = useState([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [addAddonDialogOpen, setAddAddonDialogOpen] = useState(false);
  const [removeAddonDialogOpen, setRemoveAddonDialogOpen] = useState(false);
  const [pendingAddonKey, setPendingAddonKey] = useState(null);
  const [pendingAddonName, setPendingAddonName] = useState(null);
  const [addAddonQuantity, setAddAddonQuantity] = useState(1);
  const [updatingAddonKey, setUpdatingAddonKey] = useState(null);
  const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);
  const [legalModal, setLegalModal] = useState({ open: false, fileName: null, title: '' });
  const { user } = useAuth();
  const hasHandledSuccess = React.useRef(false);

  useEffect(() => {
    if (businessId) {
      fetchAllData();
    }
    
    // Handle early termination fee payment return
    if (searchParams.get('earlyTerminationFeePaid')) {
      if (businessId) {
        // Show full-page loading state (like subscription creation)
        setLoading(true);
        setSuccess('Processing early termination fee payment and cancellation...');
        
        // Get checkout session ID from URL
        const checkoutSessionId = searchParams.get('session_id');
        
        // Track if manual processing succeeded
        let manualProcessingSucceeded = false;
        let pollTimeoutId = null;
        let pollIntervalId = null;
        
        // If we have a session ID, manually process the payment (webhook may not fire)
        if (checkoutSessionId) {
          // Wrap async code in an async function
          (async () => {
            try {
              const _processResponse = await api.post(`/api/business/${businessId}/subscriptions/process-early-termination-fee`, {
                checkoutSessionId: checkoutSessionId
              });

              // Mark as succeeded
              manualProcessingSucceeded = true;

              // Stop polling if it was started
              if (pollTimeoutId) {
                clearTimeout(pollTimeoutId);
              }
              if (pollIntervalId) {
                clearInterval(pollIntervalId);
              }

              // Refresh data and show success
              await fetchAllData().catch(() => {});
              setLoading(false);
              setSuccess('Early termination fee payment successful! Your subscription has been cancelled. Access will continue until the contract end date, but no further charges will be made.');

              // Clean up URL parameters
              window.history.replaceState({}, '', window.location.pathname + window.location.search.replace(/[?&]earlyTerminationFeePaid=true/, '').replace(/[?&]tab=subscriptions/, '').replace(/[?&]session_id=[^&]*/, ''));
            } catch (processError) {}
          })();
        }
        
        // Fallback: Poll for subscription status update (in case webhook processes it or manual processing failed)
        // Use a more efficient polling strategy: only check subscription status, not all data
        let pollCount = 0;
        const maxPolls = 15; // Poll for up to 15 seconds
        
        // Start polling after a short delay to give manual processing time to complete
        pollTimeoutId = setTimeout(() => {
          // Only start polling if manual processing hasn't succeeded
          if (manualProcessingSucceeded) {
            return;
          }
          
          pollIntervalId = setInterval(async () => {
            // Check if manual processing succeeded during polling
            if (manualProcessingSucceeded) {
              clearInterval(pollIntervalId);
              return;
            }
            
            pollCount++;
            
            // Only check subscription status, don't fetch all data (prevents rate limiting)
            try {
              const subscriptionResponse = await api.get(`/api/business/${businessId}/subscriptions`).catch((err) => {
                // If backend is down, stop polling
                if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
                  return null; // Signal that backend is down
                }
                return { data: { members: [] } };
              });
              
              // If backend is down, stop polling
              if (!subscriptionResponse) {
                clearInterval(pollIntervalId);
                setLoading(false);
                setError('Backend server is not responding. Please refresh the page or contact support if the issue persists.');
                window.history.replaceState({}, '', window.location.pathname + window.location.search.replace(/[?&]earlyTerminationFeePaid=true/, '').replace(/[?&]tab=subscriptions/, '').replace(/[?&]session_id=[^&]*/, ''));
                return;
              }
              
              const members = subscriptionResponse.data.members || [];
              
              // Check for cancelled members - multiple indicators
              const hasCancelledMember = members.some(m => {
                const isCancelled = m.subscriptionStatus === 'canceled' 
                  || m.subscriptionStatus === 'canceling'
                  || (!m.isActive && (m.subscriptionStatus || m.removedAt))
                  || m.removedAt;
                return isCancelled;
              });
              
              if (hasCancelledMember) {
                clearInterval(pollIntervalId);
                // Now fetch all data once to update the UI
                await fetchAllData().catch(() => {}); // Ignore errors on final fetch
                setLoading(false); // Hide full-page loading
                setSuccess('Early termination fee payment successful! Your subscription has been cancelled. Access will continue until the contract end date, but no further charges will be made.');
                // Clean up URL parameters
                window.history.replaceState({}, '', window.location.pathname + window.location.search.replace(/[?&]earlyTerminationFeePaid=true/, '').replace(/[?&]tab=subscriptions/, '').replace(/[?&]session_id=[^&]*/, ''));
              } else if (pollCount >= maxPolls) {
                clearInterval(pollIntervalId);
                // Final fetch to update UI
                await fetchAllData().catch(() => {}); // Ignore errors on final fetch
                setLoading(false); // Hide full-page loading
                setError('Payment processed, but cancellation status is still updating. Please refresh the page in a few moments or contact support if the issue persists.');
                // Clean up URL parameters
                window.history.replaceState({}, '', window.location.pathname + window.location.search.replace(/[?&]earlyTerminationFeePaid=true/, '').replace(/[?&]tab=subscriptions/, '').replace(/[?&]session_id=[^&]*/, ''));
              }
              // Continue polling if not cancelled yet
            } catch (error) {
              // If it's a network error, backend is down - stop polling
              if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error') || error.message.includes('CONNECTION_REFUSED')) {
                clearInterval(pollIntervalId);
                setLoading(false);
                setError('Backend server is not responding. Please refresh the page or contact support.');
                window.history.replaceState({}, '', window.location.pathname + window.location.search.replace(/[?&]earlyTerminationFeePaid=true/, '').replace(/[?&]tab=subscriptions/, '').replace(/[?&]session_id=[^&]*/, ''));
                return;
              }

              // Continue polling unless we've hit max polls
              if (pollCount >= maxPolls) {
                clearInterval(pollIntervalId);
                setLoading(false); // Hide full-page loading
                setError('Error checking cancellation status. Please refresh the page.');
                window.history.replaceState({}, '', window.location.pathname + window.location.search.replace(/[?&]earlyTerminationFeePaid=true/, '').replace(/[?&]tab=subscriptions/, '').replace(/[?&]session_id=[^&]*/, ''));
              }
            }
          }, 2000); // Poll every 2 seconds (reduced frequency to prevent rate limiting)
        }, 1000); // Wait 1 second before starting polling (gives manual processing time)
        
        // Cleanup on unmount
        return () => {
          if (pollTimeoutId) {
            clearTimeout(pollTimeoutId);
          }
          if (pollIntervalId) {
            clearInterval(pollIntervalId);
          }
          setLoading(false);
        };
      }
    } else if (searchParams.get('success') && !hasHandledSuccess.current) {
      // Handle regular subscription checkout success (only once)
      if (businessId) {
        hasHandledSuccess.current = true; // Mark as handled to prevent re-execution
        
        // Clean up URL parameter immediately to prevent re-execution on re-renders
        const currentSearch = window.location.search;
        const cleanSearch = currentSearch
          .replace(/[?&]success=true/, '')
          .replace(/[?&]session_id=[^&]*/, '')
          .replace(/^&/, '?')
          .replace(/^\?$/, '');
        window.history.replaceState({}, '', window.location.pathname + (cleanSearch || ''));
        
        // Try to sync subscription manually as fallback if webhook didn't fire
        const sessionId = searchParams.get('session_id') || localStorage.getItem('pendingSubscriptionSessionId');
        if (sessionId) {
          localStorage.removeItem('pendingSubscriptionSessionId'); // Clean up
          syncSubscription(sessionId);
        } else {
          // Wait a bit for webhook, then fetch
          setSuccess('Processing your subscription...');
          setTimeout(() => {
            fetchAllData();
            setSuccess('Thank you for your subscription! Your account has been upgraded.');
          }, 2000);
        }
      }
    } else if (searchParams.get('canceled')) {
      localStorage.removeItem('pendingSubscriptionSessionId'); // Clean up on cancel
      if (searchParams.get('reason') === 'earlyTerminationFee') {
        setError('Early termination fee payment was cancelled.');
      } else {
        setError('Subscription process was cancelled.');
      }
    }
    
    // Reset ref when businessId changes or component unmounts
    return () => {
      if (!businessId) {
        hasHandledSuccess.current = false;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId, searchParams]);

  // Refetch invoices when subscription changes
  useEffect(() => {
    if (subscription && businessId) {
      fetchInvoices();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [subscription?.stripeCustomerId, businessId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchPlans(),
        fetchSubscriptionDetails(),
        fetchInvoices(),
        fetchAddons(),
      ]);
    } catch (error) {
      setError('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const fetchAddons = async () => {
    if (!businessId) return;
    
    try {
      setLoadingAddons(true);
      const response = await api.get(`/api/business/${businessId}/subscriptions/addons`);
      setAddons(response.data.addons || []);
    } catch (error) {} finally {
      setLoadingAddons(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/api/billing/plans');

      // Filter for supplier plans - backend should already filter, but double-check
      // Accept plans with userType='supplier' OR 'both', OR billingModel='per_user'
      const supplierPlans = response.data.filter(plan => {
        const isSupplierPlan = plan.userType === 'supplier' || plan.userType === 'both';
        const isPerUserBilling = plan.billingModel === 'per_user';
        const matches = isSupplierPlan || isPerUserBilling;
        
        if (matches) {} else {}
        
        return matches;
      });

      // Sort plans: free plan first, then by sortOrder/tier
      const sortedPlans = supplierPlans.sort((a, b) => {
        // Free plan (supplier_free or price === 0) always comes first
        const aIsFree = a.key === 'supplier_free' || a.price === 0;
        const bIsFree = b.key === 'supplier_free' || b.price === 0;
        
        if (aIsFree && !bIsFree) return -1;
        if (!aIsFree && bIsFree) return 1;
        
        // For paid plans, sort by sortOrder, then tier
        if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
          return a.sortOrder - b.sortOrder;
        }
        return (a.tier || 0) - (b.tier || 0);
      });

      setPlans(sortedPlans);
    } catch (error) {
      setError('Failed to load plans');
    }
  };

  const syncSubscription = async (sessionId) => {
    if (!businessId || !sessionId) return;
    
    setSyncing(true);
    setLoading(true);
    setSuccess('Syncing your subscription...');
    setError('');
    
    try {
      const _response = await api.post(`/api/business/${businessId}/subscriptions/sync`, { sessionId });

      // Fetch updated data immediately after sync
      await fetchAllData();

      setSuccess('Thank you for your subscription! Your account has been upgraded.');

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess('');
      }, 5000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to sync subscription. Please refresh the page.');

      // Still try to fetch - webhook might have processed it
      setTimeout(async () => {
        await fetchAllData();
        // Check if subscription exists now
        if (subscription?.members?.length > 0) {
          setError('');
          setSuccess('Subscription found! Your account has been upgraded.');
        }
      }, 2000);
    } finally {
      setSyncing(false);
      setLoading(false);
    }
  };

  const fetchSubscriptionDetails = async () => {
    if (!businessId) {
      return;
    }
    try {
      const response = await api.get(`/api/business/${businessId}/subscriptions`);
      setSubscription(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {}
    }
  };

  const fetchInvoices = async () => {
    try {
      // Fetch invoices for the business's Stripe customer
      // The billing API will return invoices for the current user's Stripe customer
      const invoicesResponse = await api.get('/api/billing/invoices').catch(() => ({ data: [] }));
      let allInvoices = invoicesResponse.data || [];
      
      // If we have a business subscription with a Stripe customer ID, fetch those invoices too
      // Note: This would require a backend endpoint, so we'll use the existing API
      // The existing API should handle business customer IDs if passed correctly
      
      // Sort by date (newest first)
      allInvoices.sort((a, b) => {
        const dateA = new Date(a.date * 1000 || a.date || 0);
        const dateB = new Date(b.date * 1000 || b.date || 0);
        return dateB - dateA;
      });
      
      setInvoices(allInvoices);
      setPaymentHistory(allInvoices); // Also set payment history for compatibility
    } catch (error) {
      setInvoices([]);
      setPaymentHistory([]);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date * 1000 || date);
    return d.toLocaleDateString('en-GB', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount, _currency = 'gbp') => {
    // Stripe amounts are in pence/cents - formatGBP handles the conversion
    return formatGBP(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'open':
      case 'draft':
        return 'warning';
      case 'void':
      case 'uncollectible':
        return 'error';
      default:
        return 'default';
    }
  };

  const handlePlanSelect = async (plan) => {
    setSelectedPlan(plan);
    setTermsAccepted(false);
    setPlanDialogOpen(true);
  };

  const handleCreateCheckout = async () => {
    if (!selectedPlan || !termsAccepted) return;

    try {
      setLoading(true);
      setError('');

      // Get current user's business member ID for authentication
      let currentUserMember = null;
      let members = [];

      try {
        const membersResponse = await api.get(`/api/business/${businessId}/members`);
        members = Array.isArray(membersResponse.data) ? membersResponse.data : [];
      } catch (membersError) {
        try {
          const membersResponse = await api.get(`/api/business-members/business/${businessId}`);
          members = membersResponse.data.members || [];
        } catch (fallbackError) {
          setError('Failed to fetch business members. Please try again.');
          setLoading(false);
          return;
        }
      }

      const userId = user?.id || user?._id;

      if (!userId) {
        setError('User ID not found. Please log in again.');
        setLoading(false);
        return;
      }

      // Find current user's member record
      currentUserMember = members.find(m => {
        const memberUserId = m.user?._id?.toString() || m.user?.toString();
        return memberUserId === userId.toString();
      });

      if (!currentUserMember) {
        setError('You must be a member of this business to create a subscription. Please contact support.');
        setLoading(false);
        return;
      }

      const businessMemberId = currentUserMember._id;

      const response = await api.post(`/api/business/${businessId}/subscriptions/checkout`, {
        membershipLevelKey: selectedPlan.key,
        businessMemberId: businessMemberId,
        userId: userId,
        termsAccepted: true,
        billingCycle: 'monthly' // Always monthly - 12 month contract
      });

      if (!response.data || !response.data.url) {
        setError('Failed to create checkout session. No redirect URL received.');
        setLoading(false);
        return;
      }

      // Store sessionId in localStorage for sync after redirect
      if (response.data.sessionId) {
        localStorage.setItem('pendingSubscriptionSessionId', response.data.sessionId);
      }

      window.location.href = response.data.url;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create checkout session. Please try again.';
      setError(errorMessage);
      setLoading(false);
      // Don't close dialog on error - let user see the error message
    }
  };

  // Helper function to format feature names from snake_case to readable format
  const formatFeatureName = (feature) => {
    if (typeof feature !== 'string') return feature;
    // Convert snake_case to Title Case
    return feature
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to get storage limit based on plan key
  const getStorageLimit = (planKey) => {
    switch (planKey) {
      case 'supplier_free':
        return '50MB';
      case 'pro':
        return '2GB';
      case 'planner':
        return '10GB';
      default:
        return null;
    }
  };

  // Helper function to generate automated features based on plan limits
  const _getAutomatedFeatures = (plan) => {
    const features = [];
    
    // Add AI message limit feature
    // Check if aiMessageLimit exists and is a valid number (including 0)
    const aiLimitValue = plan.aiMessageLimit;
    if (aiLimitValue !== null && aiLimitValue !== undefined && aiLimitValue !== '') {
      const aiLimit = Number(aiLimitValue);
      if (!isNaN(aiLimit) && aiLimit >= 0) {
        features.push(`${aiLimit.toLocaleString()} AI messages per user/month`);
      }
    }
    
    // Add storage limit feature
    const storageLimit = getStorageLimit(plan.key);
    if (storageLimit) {
      features.push(`${storageLimit} storage`);
    }
    
    return features;
  };

  const renderPlansTab = () => {
    const currentPlan = subscription?.businessSubscription?.currentPlanMembershipLevelKey ||
                       subscription?.members?.find(m => m.isActive)?.membershipLevel ||
                       'supplier_free';

    const freePlan = plans.find(p => p.key === 'supplier_free');
    const individualPlan = plans.find(p => p.key === 'supplier_individual');
    const teamPlan = plans.find(p => p.key === 'supplier_team');

    const hasReferralCode = user?.referralCode && user.referralCode.trim() !== '';

    const planColumns = [
      {
        key: 'supplier_free',
        plan: freePlan,
        header: 'FREE',
        price: 'Free',
        isCurrent: currentPlan === 'supplier_free'
      },
      {
        key: 'supplier_individual',
        plan: individualPlan,
        header: 'Individual',
        price: '£49.99+VAT pm',
        isCurrent: currentPlan === 'supplier_individual'
      },
      {
        key: 'supplier_team',
        plan: teamPlan,
        header: 'Team',
        price: '£139.99+VAT pm',
        isCurrent: currentPlan === 'supplier_team'
      }
    ];

    const comparisonRows = [
      { feature: 'Users', free: '1', individual: '1', team: '3' },
      { feature: 'Profile & Messaging', free: '✓', individual: '✓', team: '✓' },
      { feature: 'Booking Management', free: 'Basic', individual: 'Full', team: 'Full' },
      { feature: 'Couple Management', free: 'Basic', individual: 'Full', team: 'Full' },
      { feature: 'Staff Scheduler', free: '—', individual: 'Up to 3 schedules', team: 'Unlimited schedules' },
      { feature: 'Lumi AI messages', free: '50 pm', individual: '500 pm', team: '2000 pm' },
      { feature: 'Calendar Sync', free: '—', individual: '✓', team: '✓' },
      { feature: 'Invoicing', free: '—', individual: '✓', team: '✓' },
      { feature: 'Team Management', free: '—', individual: '—', team: '✓' },
      { feature: 'Add on: 5000 AI Messages', free: '—', individual: '+ £19.99+VAT pm', team: '+ £19.99+VAT pm' },
      { feature: 'Add on: Wedding Co-ordinator', free: '—', individual: '+ £39.99+VAT pm', team: '+ £99.99+VAT pm' },
      { feature: 'Add on: Additional Users', free: '—', individual: '—', team: '+ £39.99+VAT user/pm (max 4)' }
    ];

    return (
      <Box>
        {hasReferralCode && (
          <Alert
            severity="info"
            icon={<Tag size={24} />}
            sx={{
              mb: 3,
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
              '& .MuiAlert-icon': { color: 'primary.contrastText' }
            }}
          >
            <Typography variant="body1" fontWeight={600} gutterBottom>
              🎉 Referral Discount Applied!
            </Typography>
            <Typography variant="body2">
              You'll receive <strong>5% off</strong> your subscription for the <strong>first 3 months</strong> when you upgrade to any paid plan.
              The discount will be automatically applied at checkout.
            </Typography>
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ overflowX: 'auto', mb: 2 }}>
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Feature</TableCell>
                {planColumns.map((col) => (
                  <TableCell key={col.key} align="center" sx={{ fontWeight: 600, minWidth: 140 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>{col.header}</Typography>
                      <Typography variant="body2" color="primary.main">{col.price}</Typography>
                      {col.plan && (
                        <Box sx={{ mt: 1 }}>
                          {col.isCurrent ? (
                            <Button variant="outlined" size="small" disabled>Current Plan</Button>
                          ) : col.key === 'supplier_free' ? null : (
                            currentPlan === 'supplier_team' && col.key === 'supplier_individual' ? (
                              <Typography variant="caption" color="text.secondary">
                                Contact support to downgrade
                              </Typography>
                            ) : (
                              <Button
                                variant={col.key === 'supplier_individual' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => handlePlanSelect(col.plan)}
                                disabled={loading}
                              >
                                Select Plan
                              </Button>
                            )
                          )}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisonRows.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={{ fontWeight: 500 }}>{row.feature}</TableCell>
                  <TableCell align="center">{row.free}</TableCell>
                  <TableCell align="center">{row.individual}</TableCell>
                  <TableCell align="center">{row.team}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const handleAddAddonClick = (addonKey, addonName) => {
    setPendingAddonKey(addonKey);
    setPendingAddonName(addonName);
    setAddAddonQuantity(1);
    setAddAddonDialogOpen(true);
  };

  const handleAddAddon = async () => {
    if (!businessId || !pendingAddonKey) return;

    const isAdditionalUsers = pendingAddonKey === 'additional_users';

    try {
      setLoading(true);
      setAddAddonDialogOpen(false);
      if (isAdditionalUsers) {
        await api.post(`/api/business/${businessId}/subscriptions/additional-seats`, {
          quantity: addAddonQuantity
        });
      } else {
        await api.post(`/api/business/${businessId}/subscriptions/addons`, {
          addonKey: pendingAddonKey,
          quantity: addAddonQuantity
        });
      }
      setSuccess(isAdditionalUsers ? 'Additional seats added successfully.' : 'Addon added successfully. Invoice created and payment processed.');
      
      // Refresh all data including invoices
      await Promise.all([
        fetchAddons(),
        fetchSubscriptionDetails(),
        fetchInvoices() // Refresh invoices to show the new invoice
      ]);
      
      // Trigger BusinessSubscriptions refresh by updating refresh key
      setSubscriptionRefreshKey(prev => prev + 1);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add addon');
    } finally {
      setLoading(false);
      setPendingAddonKey(null);
      setPendingAddonName(null);
      setAddAddonQuantity(1);
    }
  };

  const handleUpdateAddonQuantity = async (addonKey, newQuantity) => {
    if (!businessId || !addonKey) return;
    if (newQuantity < 0) return;

    const isAdditionalUsers = addonKey === 'additional_users';
    const currentQty = addons?.find(a => a.key === addonKey)?.quantity || 0;
    const toAdd = newQuantity - currentQty;

    try {
      setUpdatingAddonKey(addonKey);
      let response;
      if (isAdditionalUsers) {
        if (toAdd > 0) {
          response = await api.post(`/api/business/${businessId}/subscriptions/additional-seats`, {
            quantity: toAdd
          });
        } else if (toAdd < 0) {
          response = await api.put(`/api/business/${businessId}/subscriptions/additional-seats`, {
            quantity: Math.abs(toAdd)
          });
        } else if (newQuantity === 0) {
          response = await api.delete(`/api/business/${businessId}/subscriptions/additional-seats`);
        } else return;
      } else {
        response = await api.put(`/api/business/${businessId}/subscriptions/addons/${addonKey}`, {
          quantity: newQuantity
        });
      }
      const isReduction = toAdd < 0;
      const reduceMsg = 'This change will take effect at the start of your next billing cycle. You\'ve already paid for the current period.';
      setSuccess(isReduction ? (response?.data?.message || reduceMsg) : 'Addon quantity updated successfully.');
      await Promise.all([
        fetchAddons(),
        fetchSubscriptionDetails(),
        fetchInvoices()
      ]);
      setSubscriptionRefreshKey(prev => prev + 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update addon quantity');
    } finally {
      setUpdatingAddonKey(null);
    }
  };

  const handleCancelScheduledReduction = async (addonKey) => {
    if (!businessId || !addonKey) return;
    const isAdditionalUsers = addonKey === 'additional_users';
    try {
      setUpdatingAddonKey(addonKey);
      const url = isAdditionalUsers
        ? `/api/business/${businessId}/subscriptions/additional-seats/schedule-reduction`
        : `/api/business/${businessId}/subscriptions/addons/${addonKey}/schedule-reduction`;
      await api.delete(url);
      setSuccess('Scheduled reduction cancelled. Your current quantity will remain unchanged.');
      await Promise.all([fetchAddons(), fetchSubscriptionDetails(), fetchInvoices()]);
      setSubscriptionRefreshKey((prev) => prev + 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel scheduled reduction');
    } finally {
      setUpdatingAddonKey(null);
    }
  };

  const handleRemoveAddonClick = (addonKey, addonName) => {
    setPendingAddonKey(addonKey);
    setPendingAddonName(addonName);
    setRemoveAddonDialogOpen(true);
  };

  const handleRemoveAddon = async () => {
    if (!businessId || !pendingAddonKey) return;

    const isAdditionalUsers = pendingAddonKey === 'additional_users';

    try {
      setLoading(true);
      setRemoveAddonDialogOpen(false);
      const url = isAdditionalUsers
        ? `/api/business/${businessId}/subscriptions/additional-seats`
        : `/api/business/${businessId}/subscriptions/addons/${pendingAddonKey}`;
      const response = await api.delete(url);
      const message = response.data?.message || (isAdditionalUsers ? 'Additional seats scheduled for removal at end of billing period' : 'Addon scheduled for cancellation');
      setSuccess(message);
      
      // Refresh all data
      await Promise.all([
        fetchAddons(),
        fetchSubscriptionDetails(),
        fetchInvoices()
      ]);
      
      // Trigger BusinessSubscriptions refresh
      setSubscriptionRefreshKey(prev => prev + 1);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to remove addon');
    } finally {
      setLoading(false);
      setPendingAddonKey(null);
      setPendingAddonName(null);
    }
  };

  const handleReactivateAddon = async (addonKey, _addonName) => {
    if (!businessId || !addonKey) return;

    const isAdditionalUsers = addonKey === 'additional_users';

    try {
      setLoading(true);
      const url = isAdditionalUsers
        ? `/api/business/${businessId}/subscriptions/additional-seats/reactivate`
        : `/api/business/${businessId}/subscriptions/addons/${addonKey}/reactivate`;
      await api.post(url);
      setSuccess('Addon reactivated successfully. Invoice created and payment processed.');
      
      // Refresh all data
      await Promise.all([
        fetchAddons(),
        fetchSubscriptionDetails(),
        fetchInvoices()
      ]);
      
      // Trigger BusinessSubscriptions refresh
      setSubscriptionRefreshKey(prev => prev + 1);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to reactivate addon');
    } finally {
      setLoading(false);
    }
  };

  const renderAddOns = () => {
    if (loadingAddons) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      );
    }

    if (!addons || addons.length === 0) {
      return (
        <Alert severity="info">
          No addons available for your current plan. Upgrade to a paid plan to access addons.
        </Alert>
      );
    }

    return (
      <Grid container spacing={3}>
        {addons.map((addon) => (
          <Grid item xs={12} md={6} lg={4} key={addon.key}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: addon.isActive && !addon.isCancelled ? 'primary.main' : addon.isCancelled ? 'warning.main' : 'divider',
                borderRadius: 1,
                position: 'relative'
              }}
            >
              {addon.isActive && !addon.isCancelled && (
                <Chip
                  label="Active"
                  color="primary"
                  size="small"
                  sx={{ position: 'absolute', top: 16, right: 16 }}
                />
              )}
              {addon.isCancelled && (
                <Chip
                  label="Cancelled"
                  color="warning"
                  size="small"
                  sx={{ position: 'absolute', top: 16, right: 16 }}
                />
              )}
              <CardContent sx={{ flexGrow: 1, pt: (addon.isActive || addon.isCancelled) ? 6 : 2 }}>
                <Typography variant="h6" gutterBottom>
                  {addon.displayName || addon.name}
                </Typography>
                <Typography variant="h4" color="primary" gutterBottom>
                  {addon.price !== null && addon.price !== undefined ? (
                    <>
                      {formatGBPFromPounds(addon.isQuantityBased && addon.quantity > 1 ? addon.price * addon.quantity : addon.price)}
                      {addon.isQuantityBased && addon.quantity > 1 && (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}({addon.quantity} × {formatGBPFromPounds(addon.price)})
                        </Typography>
                      )}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {getPriceSuffix(addon.price, 'month')}
                      </Typography>
                    </>
                  ) : (
                    <Typography component="span" variant="body1" color="text.secondary">
                      POA
                    </Typography>
                  )}
                </Typography>
                {addon.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {addon.description}
                  </Typography>
                )}
                {addon.isCancelled && addon.scheduledDeletionDate && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      Expires: {new Date(addon.scheduledDeletionDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      This addon will be cancelled at the end of your billing period
                    </Typography>
                  </Alert>
                )}
                {addon.scheduledQuantity != null && addon.isActive && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      Your current plan: {addon.quantity || 1} {addon.key === 'additional_users' ? (addon.quantity === 1 ? 'seat' : 'seats') : (addon.quantity === 1 ? 'message' : 'messages')} at {formatGBPFromPounds((addon.price || 0) * (addon.quantity || 1))}/month
                    </Typography>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      Your renewal will be: {addon.scheduledQuantity} {addon.key === 'additional_users' ? (addon.scheduledQuantity === 1 ? 'seat' : 'seats') : (addon.scheduledQuantity === 1 ? 'message' : 'messages')} at {formatGBPFromPounds((addon.price || 0) * addon.scheduledQuantity)}/month
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      You can cancel this at any point before the renewal date. Renewal: {subscription?.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleCancelScheduledReduction(addon.key)}
                      disabled={updatingAddonKey === addon.key}
                    >
                      {updatingAddonKey === addon.key ? 'Cancelling...' : 'Cancel this change'}
                    </Button>
                  </Alert>
                )}
                {addon.unlocksFeatures && addon.unlocksFeatures.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Unlocks:
                    </Typography>
                    <Stack spacing={0.5}>
                      {addon.unlocksFeatures.map((feature, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          • {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
              <Box sx={{ p: 2, pt: 0 }}>
                {addon.isCancelled ? (
                  <Button 
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => handleReactivateAddon(addon.key, addon.displayName || addon.name)}
                    disabled={loading}
                  >
                    Re-activate this addon
                  </Button>
                ) : addon.isActive ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {addon.isQuantityBased && addon.maxQuantity && (
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                        <Tooltip
                          title={
                            addon.key === 'additional_users' && (addon.quantity || 1) <= (addon.filledSeats || 0)
                              ? 'Remove team members from Staff first to reduce seats'
                              : 'Decrease quantity'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const current = addon.scheduledQuantity != null ? addon.scheduledQuantity : (addon.quantity || 1);
                                handleUpdateAddonQuantity(addon.key, Math.max(0, current - 1));
                              }}
                              disabled={
                                updatingAddonKey === addon.key ||
                                (addon.scheduledQuantity != null ? addon.scheduledQuantity : (addon.quantity || 1)) <= 0 ||
                                (addon.key === 'additional_users' && (addon.scheduledQuantity != null ? addon.scheduledQuantity : (addon.quantity || 1)) <= (addon.filledSeats || 0))
                              }
                              aria-label="Decrease quantity"
                            >
                              <Minus size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Typography variant="body2" fontWeight={500} sx={{ minWidth: 20, textAlign: 'center' }}>
                          {addon.scheduledQuantity != null ? addon.scheduledQuantity : (addon.quantity || 1)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateAddonQuantity(addon.key, Math.min(addon.maxQuantity, (addon.quantity || 1) + 1))}
                          disabled={
                            addon.scheduledQuantity != null ||
                            (addon.quantity || 1) >= addon.maxQuantity ||
                            updatingAddonKey === addon.key
                          }
                          aria-label={addon.key === 'additional_users' ? 'Add seat' : 'Increase quantity'}
                        >
                          <Plus size={18} />
                        </IconButton>
                        {updatingAddonKey === addon.key && (
                          <CircularProgress size={16} sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    )}
                    {addon.key !== 'additional_users' && (
                      <Button 
                        variant="outlined"
                        color="error"
                        fullWidth
                        onClick={() => handleRemoveAddonClick(addon.key, addon.displayName || addon.name)}
                        disabled={loading}
                        startIcon={<Trash size={16} />}
                      >
                        Remove Addon
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Button 
                    variant="contained"
                    fullWidth
                    onClick={() => handleAddAddonClick(addon.key, addon.displayName || addon.name)}
                    disabled={loading || addon.price === null || addon.price === undefined}
                  >
                    Add Addon
                  </Button>
                )}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderInvoicesTab = () => {
    const rawInvoices = invoices.length > 0 ? invoices : paymentHistory;
    // Filter out £0 and credit (negative) invoices from proration flow
    const allInvoices = rawInvoices.filter((inv) => {
      const amt = inv.netAmount ?? inv.amount ?? 0;
      return amt > 0;
    });

    return (
      <Box>
        <Box mb={3}>
          <Typography variant="h5">
            Invoice & Payment History
          </Typography>
        </Box>

        {allInvoices.length === 0 ? (
          <Card>
            <CardContent>
              <Box textAlign="center" py={4}>
                <Receipt size={48} color="text.secondary" style={{ margin: '0 auto 16px', opacity: 0.5 }} />
                <Typography variant="body1" color="text.secondary">
                  No invoices or payment history available yet.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allInvoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      {formatDate(invoice.date || invoice.created)}
                    </TableCell>
                    <TableCell>
                      {invoice.periodStart && invoice.periodEnd
                        ? `Subscription ${formatDate(invoice.periodStart)} - ${formatDate(invoice.periodEnd)}`
                        : 'Subscription Payment'}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(invoice.netAmount !== undefined ? invoice.netAmount : invoice.amount, invoice.currency)}
                          {invoice.currency && invoice.currency.toUpperCase() !== 'GBP' && ` ${invoice.currency.toUpperCase()}`}
                        </Typography>
                        {invoice.hasProratedAddon && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            Prorated for remaining time in month
                          </Typography>
                        )}
                        {invoice.refundAmount > 0 && (
                          <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                            Refunded: {formatCurrency(invoice.refundAmount, invoice.currency)}
                          </Typography>
                        )}
                        {invoice.refundAmount > 0 && invoice.amount && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textDecoration: 'line-through', opacity: 0.6 }}>
                            Original: {formatCurrency(invoice.amount, invoice.currency)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status || 'paid'}
                        color={getStatusColor(invoice.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {(invoice.hostedInvoiceUrl || invoice.receiptUrl) && (
                          <Button
                            size="small"
                            href={invoice.hostedInvoiceUrl || invoice.receiptUrl}
                            target="_blank"
                            startIcon={<Receipt size={16} />}
                            variant={invoice.status === 'paid' ? 'outlined' : 'contained'}
                            color={invoice.status === 'paid' ? 'primary' : 'primary'}
                          >
                            {invoice.status === 'paid' ? 'View Invoice' : 'Pay Online'}
                          </Button>
                        )}
                        {invoice.invoicePdf && (
                          <Button
                            size="small"
                            href={invoice.invoicePdf}
                            target="_blank"
                            startIcon={<Download size={16} />}
                            variant="outlined"
                          >
                            Download PDF
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  if (!hasPermission('view_financials')) {
    return (
      <Alert severity="error">
        You don't have permission to view billing information.
      </Alert>
    );
  }

  // Show full-page loading when processing early termination fee payment
  const isProcessingEarlyTerminationFee = searchParams.get('earlyTerminationFeePaid') === 'true';
  
  if (loading && !subscription && !syncing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Show syncing overlay
  if (syncing || (loading && (searchParams.get('success') || isProcessingEarlyTerminationFee))) {
    return (
      <Box>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" flexDirection="column" gap={2}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            {syncing 
              ? 'Syncing your subscription...' 
              : isProcessingEarlyTerminationFee 
                ? 'Processing cancellation and payment...' 
                : 'Processing your subscription...'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isProcessingEarlyTerminationFee 
              ? 'Please wait while we finalise your cancellation and update your account'
              : 'Please wait while we update your account'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Syncing overlay - prevents interaction while syncing */}
      {syncing && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 2,
            borderRadius: 1
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Syncing your subscription...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we update your account
          </Typography>
        </Box>
      )}

      <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, mb: 3 }}>
        Billing
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Plans Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Plans & Pricing
        </Typography>
        {renderPlansTab()}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Add Ons Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Add Ons
        </Typography>
        {renderAddOns()}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Billing Section */}
      <Box sx={{ mb: 4 }}>
        <BusinessSubscriptions 
          key={subscriptionRefreshKey}
          onSubscriptionUpdate={() => {
            fetchSubscriptionDetails();
            fetchInvoices();
          }}
        />
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Invoices Section */}
      <Box sx={{ mb: 4 }}>
        {renderInvoicesTab()}
      </Box>

      {/* Plan Selection Dialog */}
      <Dialog 
        open={planDialogOpen} 
        onClose={() => {
          if (!loading) {
            setPlanDialogOpen(false);
            setError('');
            setTermsAccepted(false);
          }
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Confirm Plan Selection
          </Typography>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {selectedPlan && (
            <Box>
              {/* Plan Summary Card */}
              <Box 
                sx={{ 
                  p: 3, 
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  borderRadius: 2,
                  mb: 3,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  {selectedPlan.displayName || selectedPlan.name}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  {selectedPlan.price === null || selectedPlan.price === undefined 
                    ? 'POA' 
                    : formatGBPFromPounds(selectedPlan.price)}
                  {selectedPlan.price !== null && selectedPlan.price !== undefined && (
                    <Typography component="span" variant="body2" sx={{ ml: 0.5, opacity: 0.8 }}>
                      {getPriceSuffix(selectedPlan.price, 'month')}
                    </Typography>
                  )}
                </Typography>
                {selectedPlan.contractTermMonths && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.8 }}>
                    {selectedPlan.contractTermMonths}-month contract
                  </Typography>
                )}
              </Box>

              {/* Plan Description */}
              {selectedPlan.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {selectedPlan.description}
                </Typography>
              )}

              {/* Plan Features */}
              {selectedPlan.features && selectedPlan.features.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    What's included:
                  </Typography>
                  <Stack spacing={1}>
                    {selectedPlan.features.map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckIcon sx={{ color: 'success.main', fontSize: 20, mt: 0.25 }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatFeatureName(feature)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* User Limit Info */}
              {selectedPlan.maxUsers !== null && selectedPlan.maxUsers !== undefined && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Team Size:</strong> This plan includes up to {selectedPlan.maxUsers === 1 ? '1 user' : `${selectedPlan.maxUsers} team members`}.
                    {selectedPlan.maxUsers === 1 && (
                      <> Upgrade to Team plan to invite additional members.</>
                    )}
                    {selectedPlan.maxUsers > 1 && selectedPlan.maxUsers < 7 && (
                      <> Additional members can be added for {formatGBPFromPounds(selectedPlan.additionalUserPrice || 39.99)}{getPriceSuffix(selectedPlan.additionalUserPrice || 39.99, 'month')} each.</>
                    )}
                  </Typography>
                </Alert>
              )}

              {/* Terms and Conditions Acceptance */}
              <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      required
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                    />
                  }
                  label={
                    <Typography variant="body2" component="span">
                      I agree to the{' '}
                      <Typography
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setLegalModal({ open: true, fileName: 'business-terms', title: 'Business Terms and Conditions' });
                        }}
                        sx={{
                          color: 'inherit',
                          textDecoration: 'underline',
                          fontWeight: 500,
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          p: 0,
                          fontFamily: 'inherit',
                        }}
                      >
                        Business Terms and Conditions
                      </Typography>
                      {' '}and{' '}
                      <Typography
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setLegalModal({ open: true, fileName: 'privacy-policy', title: 'Privacy Policy' });
                        }}
                        sx={{
                          color: 'inherit',
                          textDecoration: 'underline',
                          fontWeight: 500,
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          p: 0,
                          fontFamily: 'inherit',
                        }}
                      >
                        Privacy Policy
                      </Typography>
                      .
                    </Typography>
                  }
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button 
            onClick={() => {
              setPlanDialogOpen(false);
              setTermsAccepted(false);
            }}
            disabled={loading}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCheckout}
            variant="contained"
            disabled={!termsAccepted || loading}
            sx={{ minWidth: 160 }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              'Continue to Payment'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Addon Confirmation Modal */}
      <Dialog
        open={addAddonDialogOpen}
        onClose={() => {
          setAddAddonDialogOpen(false);
          setPendingAddonKey(null);
          setPendingAddonName(null);
          setAddAddonQuantity(1);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Addon Addition</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to add <strong>{pendingAddonName}</strong> to your subscription.
          </Typography>
          {(() => {
            const pendingAddon = addons?.find(a => a.key === pendingAddonKey);
            if (pendingAddon?.isQuantityBased && pendingAddon?.maxQuantity) {
              return (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Quantity (max {pendingAddon.maxQuantity})
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => setAddAddonQuantity(q => Math.max(1, q - 1))}
                      disabled={addAddonQuantity <= 1}
                      aria-label="Decrease quantity"
                    >
                      <Minus size={20} />
                    </IconButton>
                    <Typography variant="body1" fontWeight={500} sx={{ minWidth: 24, textAlign: 'center' }}>
                      {addAddonQuantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setAddAddonQuantity(q => Math.min(pendingAddon.maxQuantity, q + 1))}
                      disabled={addAddonQuantity >= pendingAddon.maxQuantity}
                      aria-label="Increase quantity"
                    >
                      <Plus size={20} />
                    </IconButton>
                  </Box>
                  {addAddonQuantity > 1 && pendingAddon.price != null && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Total: {formatGBPFromPounds(pendingAddon.price * addAddonQuantity)}{getPriceSuffix(pendingAddon.price * addAddonQuantity, 'month')}
                    </Typography>
                  )}
                </Box>
              );
            }
            return null;
          })()}
          <Alert severity="info" sx={{ mt: 2 }}>
            This addon will be automatically billed and renew each month. You can cancel it at any time, and it will remain active until the end of your current billing period.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddAddonDialogOpen(false);
              setPendingAddonKey(null);
              setPendingAddonName(null);
              setAddAddonQuantity(1);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddAddon}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Confirm & Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Addon Confirmation Modal */}
      <Dialog
        open={removeAddonDialogOpen}
        onClose={() => {
          setRemoveAddonDialogOpen(false);
          setPendingAddonKey(null);
          setPendingAddonName(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Addon Cancellation</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to cancel <strong>{pendingAddonName}</strong>.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Your addon will remain active and fully functional until the end of your current billing period. After that, it will be automatically cancelled and you will no longer be charged. You can re-add it at any time before the cancellation takes effect.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRemoveAddonDialogOpen(false);
              setPendingAddonKey(null);
              setPendingAddonName(null);
            }}
          >
            Keep Addon
          </Button>
          <Button
            onClick={handleRemoveAddon}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>

      <LegalDocumentModal
        open={legalModal.open}
        onClose={() => setLegalModal({ open: false, fileName: null, title: '' })}
        fileName={legalModal.fileName}
        title={legalModal.title}
      />
    </Box>
  );
}

export default BusinessBilling;

